<template>
  <BaseSimuladorIndicadorModal
    ref="baseModal"
    :modal-id="modalId"
    title="Simulador"
    numerador-label="Numerador"
    denominador-label="Denominador"
    :valor-numerador="nManual"
    :valor-denominador="dManual"
    :valor-resultado="valorResultado"
    :base-numerador="baseNumerador"
    :base-denominador="baseDenominador"
    :procedimentos-numerador="PROCEDIMENTOS_NUMERADOR"
    :procedimentos-denominador="PROCEDIMENTOS_DENOMINADOR"
    :tipos="TIPOS_CLASSIFICACAO"
    :calculate-result-fn="calcularResultado"
    :calculate-class-fn="calcularClasse"
    :apply-preset-fn="aplicarPreset"
    :format-delta-fn="formatDelta"
    :result-text-fn="montarTextoResultado"
    hint="💡 Os botões preenchem valores de exemplo. Ajuste manualmente pelos controles acima."
    @update:numerador="setNumerador"
    @update:denominador="setDenominador"
    :tipoResultado="tipoResultado"
  />
</template>

<script>
import BaseSimuladorIndicadorModal from '@/pages/IndicadoresAps/components/BaseSimuladorIndicadorModal.vue'

export default {
  name: 'SimuladorOdontoModal',
  components: {
    BaseSimuladorIndicadorModal,
  },
  props: {
    modalId: {
      type: String,
      default: 'simulador-odonto-modal',
    },
    dadosPercentualMunicipio: {
      type: Object,
      default: () => ({}),
    },
    dadosPercentualEquipe: {
      type: Object,
      default: () => ({}),
    },
    dadosPercentualMicroArea: {
      type: Object,
      default: () => ({}),
    },
    selectedEquipe: {
      type: [Object, String],
      default: '',
    },
    selectedMicroArea: {
      type: String,
      default: '',
    },
    tipoResultado: {
      type: String,
      default: 'quadrimestral',
    },
  },
  data() {
    return {
      nManual: 0,
      dManual: 1,
      valorResultado: 0,
      baseNumerador: 0,
      baseDenominador: 1,
    }
  },
  computed: {
    TIPOS_CLASSIFICACAO() {
      const lista = this.$store.state.regrasIndicadoresAps || []

      const indicador = 40

      const classificacoes = ['Ótimo', 'Bom', 'Suficiente', 'Regular']

      return classificacoes
        .map((classificacao) => {
          const itens =
            classificacao === 'Regular'
              ? lista.filter(
                  (i) =>
                    i.id_indicador == indicador &&
                    i.classificacao === classificacao
                )
              : [
                  lista.find(
                    (i) =>
                      i.id_indicador == indicador &&
                      i.classificacao === classificacao
                  ),
                ].filter(Boolean)

          if (!itens.length) return null

          const description = itens
            .map((item) => {
              const partes = []

              if (item.sinal_min && item.min_val) {
                partes.push(`${item.sinal_min} ${item.min_val}`)
              }

              if (item.sinal_max && item.max_val) {
                partes.push(`${item.sinal_max} ${item.max_val}`)
              }

              return partes.join(' e ')
            })
            .filter(Boolean)
            .join(' ou ')

          return {
            value: classificacao,
            label: classificacao,
            description,
          }
        })
        .filter(Boolean)
    },

    PROCEDIMENTOS_NUMERADOR() {
      return (
        this.$store.state.procedimentosIndicadoresAps?.preventivosOdonto
          ?.numerador || []
      )
    },

    PROCEDIMENTOS_DENOMINADOR() {
      return (
        this.$store.state.procedimentosIndicadoresAps?.preventivosOdonto
          ?.denominador || []
      )
    },
  },
  methods: {
    /**
     * Abre o modal do simulador.
     *
     * Carrega a fonte inicial dos valores e abre o modal.
     */
    open() {
      this.aplicarFonte()
      this.$refs.baseModal.open()
    },

    close() {
      this.$refs.baseModal.close()
    },

    aplicarFonte() {
      let fonte = this.dadosPercentualMunicipio

      if (this.selectedMicroArea) {
        fonte = this.dadosPercentualMicroArea
      } else if (this.selectedEquipe) {
        fonte = this.dadosPercentualEquipe
      }

      const numerador = Number(fonte?.numerador) || 0
      const denominador = Number(fonte?.denominador) || 1
      const resultado = Number(fonte?.resultado) || 0

      this.nManual = numerador
      this.dManual = denominador
      // No quadrimestral, o valor inicial vem da fonte.
      this.valorResultado = resultado
      this.resetBase()
    },

    setNumerador(valor) {
      this.nManual = valor
      this.valorResultado = this.calcularResultado({
        numerador: this.nManual,
        denominador: this.dManual,
      })
    },

    setDenominador(valor) {
      this.dManual = valor
      this.valorResultado = this.calcularResultado({
        numerador: this.nManual,
        denominador: this.dManual,
      })
    },

    resetBase() {
      this.baseNumerador = this.nManual
      this.baseDenominador = this.dManual
    },

    calcularResultado({ numerador, denominador }) {
      const d = Number(denominador) || 0
      if (d <= 0) return 0

      const n = Number(numerador) || 0
      return Number(((n / d) * 100).toFixed(2))
    },

    calcularClasse({ resultado }) {
      if (resultado >= 65 && resultado <= 85) {
        return { nivel: 'Ótimo', variant: 'primary' }
      }
      if (resultado >= 55 && resultado < 65) {
        return { nivel: 'Bom', variant: 'success' }
      }
      if (resultado >= 40 && resultado < 55) {
        return { nivel: 'Suficiente', variant: 'warning' }
      }
      if (resultado < 40 || resultado > 85)
        return { nivel: 'Regular', variant: 'danger' }

      return { nivel: 'Regular', variant: 'danger' }
    },

    aplicarPreset({ tipo, setNumerador, setDenominador }) {
      if (tipo === 'Ótimo') {
        setNumerador(80)
        setDenominador(100)
      } else if (tipo === 'Bom') {
        setNumerador(60)
        setDenominador(100)
      } else if (tipo === 'Suficiente') {
        setNumerador(40)
        setDenominador(100)
      } else {
        setNumerador(39)
        setDenominador(100)
      }

      this.$nextTick(() => {
        this.resetBase()
      })
    },

    formatDelta(val) {
      if (val > 0) return `+${val}`
      if (val < 0) return `${val}`
      return ''
    },

    montarTextoResultado({ resultado }) {
      return `NM / DN × 100`
    },
  },
}
</script>
